import React, { useCallback, useMemo } from 'react'
import { useJoinFunnelAnalytics } from '@components/Analytics/useJoinFunnelAnalytics'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { Button, ButtonProps, Icon, Stack } from '@ui'
import { providers, Signer } from 'ethers'
import { Analytics } from 'hooks/useAnalytics'
import { useDevice } from 'hooks/useDevice'
import { useEnvironment } from 'hooks/useEnvironment'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { usePublicPageLoginFlow } from 'routes/PublicTownPage/usePublicPageLoginFlow'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'

import { mapToErrorMessage } from '../utils'

import { useConnectedNonPrivyWallets } from './useConnectedNonPrivyWallets'
import { useConnectThenLink } from './useConnectThenLink'
import { usePrivyConnectWallet } from './usePrivyConnectWallet'

type Props = {
    onLinkWallet: (rootKey: Signer | undefined, wallet: Signer | undefined) => void
    buttonText?: string
    children?: (props: { onClick: () => void }) => React.JSX.Element
} & Omit<ButtonProps, 'children' | 'onClick'>

export function ConnectWalletThenLinkButton(props: Props) {
    const { isTouch, isSafari } = useDevice()
    return (
        <WalletReady>
            {({ getSigner }) => {
                if (isTouch || isSafari) {
                    return <ConnectAndLinkMobile {...props} getSigner={getSigner} />
                }
                return <ConnectThenLinkDesktop {...props} getSigner={getSigner} />
            }}
        </WalletReady>
    )
}

function ConnectThenLinkDesktop(props: Props & { getSigner: GetSigner }) {
    const { onLinkWallet, buttonText = 'Link Wallet', children, getSigner, ...buttonProps } = props
    const onConnect = useConnectThenLink({
        onLinkWallet,
        getSigner,
    })
    const { spaceBeingJoined } = usePublicPageLoginFlow()
    const { clickedLinkWalletOnGatedTownRequirementsModal } = useJoinFunnelAnalytics()

    const onConnectClick = useCallback(() => {
        if (spaceBeingJoined) {
            clickedLinkWalletOnGatedTownRequirementsModal({ spaceId: spaceBeingJoined })
        }
        onConnect()
    }, [onConnect, spaceBeingJoined, clickedLinkWalletOnGatedTownRequirementsModal])

    const onClick = useCallback(() => {
        onConnectClick()
    }, [onConnectClick])

    return (
        <>
            {children ? (
                children({ onClick })
            ) : (
                <Button onClick={onClick} {...buttonProps}>
                    <Icon type="link" /> {buttonText}
                </Button>
            )}
        </>
    )
}

function ConnectAndLinkMobile(props: Props & { getSigner: GetSigner }) {
    const { onLinkWallet, getSigner } = props
    const { baseChain } = useEnvironment()
    const { ready, wallets } = useConnectedNonPrivyWallets()
    const isConnected = wallets.length > 0
    const selectedWallet = isConnected ? wallets[0] : null

    const connectWallet = usePrivyConnectWallet({
        onSuccess: async ({ wallet }) => {
            Analytics.getInstance().track('connected wallet', {
                walletName: wallet.meta.name,
            })
        },
        onError: (error) => {
            console.error('[ConnectOnMobile] error connecting wallet', error)
            const errorMessage = mapToErrorMessage({
                error: new Error(error),
                source: 'ConnectOnMobile privy connect error',
            })

            if (errorMessage === 'ACTION_REJECTED') {
                return
            }

            popupToast(({ toast }) => (
                <StandardToast.Error
                    toast={toast}
                    message="There was an error connecting your wallet."
                    subMessage={errorMessage}
                />
            ))
        },
    })

    const buttonState = useMemo(() => {
        if (!isConnected || !selectedWallet) {
            return {
                text: 'Connect',
                action: () => connectWallet(),
            }
        } else if (isConnected && selectedWallet) {
            if ('type' in selectedWallet && selectedWallet.type === 'ethereum') {
                return {
                    text: 'Link Wallet',
                    action: async () => {
                        try {
                            // wallet connect seems to internally switch the chain in our app for this tx - it won't switch networks in the wallet app
                            // i believe this is because wallet connect sees our authorized chains set up in our privy config, and just allows to switch + execute the tx
                            await selectedWallet.switchChain(baseChain.id)
                            const privyProvider = await selectedWallet.getEthereumProvider()
                            const provider = new providers.Web3Provider(privyProvider)
                            const rootSigner = await getSigner()
                            const eoaSigner = provider.getSigner()
                            onLinkWallet(rootSigner, eoaSigner)
                        } catch (error) {
                            console.error('[ConnectAndLinkMobile] Error linking wallet:', error)
                            popupToast(({ toast }) => (
                                <StandardToast.Error
                                    toast={toast}
                                    message="Failed to link wallet"
                                />
                            ))
                        }
                    },
                }
            }
        }
    }, [connectWallet, getSigner, onLinkWallet, isConnected, selectedWallet, baseChain.id])

    if (!ready) {
        return <ButtonSpinner />
    }

    return (
        <Stack width="100%" gap="md">
            <Button tone="cta1" onClick={buttonState?.action}>
                <Icon type="link" />
                {buttonState?.text}
            </Button>
            {/* <ConnectedWallet wallet={selectedWallet} /> */}
        </Stack>
    )
}
